import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Gamepad2, LayoutTemplate, Users, Boxes, Loader2, CornerDownLeft, Clock } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const GROUP_META = {
  games: { label: "Games", icon: Gamepad2 },
  templates: { label: "Templates", icon: LayoutTemplate },
  creators: { label: "Creators", icon: Users },
  assets: { label: "Studio Assets", icon: Boxes },
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [scope, setScope] = useState("all");
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);
  const qRef = useRef("");
  qRef.current = q;
  const [recents, setRecents] = useState([]);

  const loadRecents = useCallback(() => {
    try { setRecents(JSON.parse(localStorage.getItem("gv_recent_searches") || "[]").slice(0, 6)); }
    catch { setRecents([]); }
  }, []);
  const pushRecent = useCallback((term) => {
    term = (term || "").trim();
    if (term.length < 2) return;
    try {
      const cur = JSON.parse(localStorage.getItem("gv_recent_searches") || "[]");
      const next = [term, ...cur.filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, 6);
      localStorage.setItem("gv_recent_searches", JSON.stringify(next));
      setRecents(next);
    } catch { /* ignore */ }
  }, []);

  // ⌘K / Ctrl+K opens the palette anywhere in the app.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 40); loadRecents(); }
    else { setQ(""); setData(null); setActive(0); setScope("all"); }
  }, [open, loadRecents]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) { setData(null); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqRef.current;
      try {
        const r = await api.get("/search/global", { params: { q: query } });
        if (myReq === reqRef.current) { setData(r.data); setActive(0); }
      } catch {
        if (myReq === reqRef.current) setData({ games: [], templates: [], creators: [], assets: [], total: 0 });
      } finally {
        if (myReq === reqRef.current) setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Flatten grouped results for keyboard navigation (respecting the active scope filter).
  const flat = useMemo(() => {
    if (!data) return [];
    const out = [];
    const add = (gk, arr, mk) => { if (scope !== "all" && scope !== gk) return; (arr || []).forEach(mk); };
    add("games", data.games, (g) => out.push({ group: "games", key: `game-${g.id}`, item: g, link: g.link }));
    add("templates", data.templates, (t) => out.push({ group: "templates", key: `template-${t.id}`, item: t, link: t.link }));
    add("creators", data.creators, (c) => out.push({ group: "creators", key: `creator-${c.id}`, item: c, link: c.link }));
    add("assets", data.assets, (a) => out.push({ group: "assets", key: `asset-${a.id}`, item: a, link: a.link }));
    return out;
  }, [data, scope]);

  const go = useCallback((link) => {
    if (!link) return;
    pushRecent(qRef.current);
    setOpen(false);
    navigate(link);
  }, [navigate, pushRecent]);

  const onKeyDown = (e) => {
    if (!flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + flat.length) % flat.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]?.link); }
  };

  const renderRow = (entry, idx) => {
    const { group, item, key, link } = entry;
    const isActive = idx === active;
    let icon, title, subtitle;
    if (group === "games") {
      icon = <span className="text-lg">{item.cover_emoji}</span>;
      title = item.title;
      subtitle = item.mine ? `Your game · ${item.status}` : `by ${item.owner_name} · ${item.dimension?.toUpperCase()}`;
    } else if (group === "templates") {
      icon = <span className="text-lg">{item.icon}</span>;
      title = item.name;
      subtitle = item.genre || "Template";
    } else if (group === "creators") {
      icon = <span className="w-7 h-7 rounded-full grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] text-sm">{item.avatar_emoji}</span>;
      title = item.name + (item.is_me ? " (you)" : "");
      subtitle = `${item.games} published game${item.games === 1 ? "" : "s"}`;
    } else {
      icon = item.thumbnail
        ? <img src={item.thumbnail} alt="" className="w-7 h-7 rounded-md object-cover" />
        : <span className="text-lg">{item.icon}</span>;
      title = item.name;
      subtitle = item.kind === "3d" ? "3D model" : item.kind === "character" ? "Character" : "Asset";
    }
    return (
      <button
        key={key}
        data-testid={`gsearch-${key}`}
        onMouseEnter={() => setActive(idx)}
        onClick={() => go(link)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
          isActive ? "bg-[hsl(var(--primary))]/12" : "hover:bg-[hsl(var(--secondary))]"
        }`}
      >
        <span className="shrink-0 w-7 h-7 grid place-items-center">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold truncate text-[hsl(var(--foreground))]">{title}</span>
          <span className="block text-xs truncate text-[hsl(var(--muted-foreground))]">{subtitle}</span>
        </span>
        {isActive && <CornerDownLeft className="w-3.5 h-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />}
      </button>
    );
  };

  const groups = ["games", "templates", "creators", "assets"];
  let runningIdx = 0;

  return (
    <>
      {/* Desktop pill trigger */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="hidden md:flex items-center gap-2 h-9 pl-3 pr-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/60 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] transition-colors w-56"
      >
        <Search className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-left text-sm">Search…</span>
        <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 h-5 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[10px] font-semibold">⌘K</kbd>
      </button>
      {/* Mobile icon trigger */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger-mobile"
        className="md:hidden grid place-items-center w-9 h-9 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]"
        aria-label="Search"
      >
        <Search className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="global-search-dialog"
          className="p-0 gap-0 max-w-xl top-[12%] translate-y-0 overflow-hidden"
        >
          <DialogTitle className="sr-only">Search GameView Studio</DialogTitle>
          <div className="flex items-center gap-2.5 px-4 border-b border-[hsl(var(--border))]">
            {loading ? <Loader2 className="w-5 h-5 shrink-0 animate-spin text-[hsl(var(--primary))]" />
                     : <Search className="w-5 h-5 shrink-0 text-[hsl(var(--muted-foreground))]" />}
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              data-testid="global-search-input"
              placeholder="Search games, templates, creators, assets…"
              className="flex-1 h-14 bg-transparent outline-none text-base placeholder:text-[hsl(var(--muted-foreground))] text-[hsl(var(--foreground))]"
            />
          </div>

          {q.trim().length >= 2 && data && data.total > 0 && (
            <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-2 flex-wrap border-b border-[hsl(var(--border))]" data-testid="gsearch-filters">
              {[["all", "All", data.total], ["games", "Games", (data.games || []).length], ["templates", "Templates", (data.templates || []).length], ["creators", "Creators", (data.creators || []).length], ["assets", "Assets", (data.assets || []).length]]
                .filter(([k, , n]) => k === "all" || n > 0)
                .map(([k, label, n]) => (
                  <button
                    key={k}
                    onClick={() => { setScope(k); setActive(0); }}
                    data-testid={`gsearch-filter-${k}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${scope === k ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
                  >
                    {label} <span className="opacity-70">{n}</span>
                  </button>
                ))}
            </div>
          )}

          <div className="max-h-[52vh] overflow-y-auto p-2">
            {q.trim().length < 2 && (
              <div className="px-2 py-3" data-testid="global-search-hint">
                {recents.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between px-1 pb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Recent searches</span>
                      <button
                        onClick={() => { try { localStorage.removeItem("gv_recent_searches"); } catch {} setRecents([]); }}
                        className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                        data-testid="global-search-clear-recents"
                      >Clear</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recents.map((t) => (
                        <button
                          key={t}
                          onClick={() => setQ(t)}
                          data-testid={`gsearch-recent-${t}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/50 text-sm hover:bg-[hsl(var(--secondary))] transition-colors"
                        >
                          <Clock className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" /> {t}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="py-7 text-center text-sm text-[hsl(var(--muted-foreground))]">
                    Search across games, templates, creators &amp; your studio assets.
                  </p>
                )}
              </div>
            )}
            {q.trim().length >= 2 && !loading && data && flat.length === 0 && (
              <div className="px-3 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="global-search-empty">
                {data.total === 0
                  ? <>No matches for “{q.trim()}”. Try a different word.</>
                  : <>No {scope === "all" ? "results" : scope} match “{q.trim()}”. Try another filter.</>}
              </div>
            )}
            {data && data.total > 0 && groups.filter((gk) => scope === "all" || gk === scope).map((gk) => {
              const rows = data[gk] || [];
              if (!rows.length) return null;
              const Meta = GROUP_META[gk];
              const Icon = Meta.icon;
              return (
                <div key={gk} className="mb-1.5" data-testid={`gsearch-group-${gk}`}>
                  <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <Icon className="w-3.5 h-3.5" /> {Meta.label}
                  </div>
                  {rows.map((it) => {
                    const entry = flat[runningIdx];
                    const node = renderRow(entry, runningIdx);
                    runningIdx += 1;
                    return node;
                  })}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
